import React, {useEffect, useState} from "react";
import {SEARCH_MODEL} from "../../../../../components/table/Model/TableModel";
import {SimpleSearchWithId} from "../../../../../reduxToolkit/Search/SearchApi";
import {useDispatch} from "react-redux";
import {TABLES} from "../../../../../Apis/Tables";
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import CustomChip from "../../../../../components/CustomChip";
import {status} from "../../../../../enums/statusEnum";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../../enums/statusColorEnum";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import {
    DetailsIconsOptions,
    IconAvatarTable, IconClip,
    IconClockTable,
    IconScheduleTable
} from "../../../../../components/buttons/IconButtons";
import TableFunction from "../../../../../components/table/TableFunction";
import {errorMsg, getDateFormat, getTimeFormat} from "../../../../../Apis/functions";
import CustomImageTable from "../../../../../components/CustomImageTable";
import {toast} from "react-toastify";
import {formatName} from "../Model/Model";
import {DownloadDataZip, ZipDownload} from "../../../../../reduxToolkit/fileUpload/FileUploadApi";

/**
 * @component RequestAccessHistoryTableApp
 * @description Renders the access request history table for a specific visitor.
 * It supports pagination, search, row actions, and displays the request status with colored chips.
 *
 * @param {Object} item - Visitor object containing the unique identifier and related information.
 *
 * @returns {Object} Table configuration, handlers, and data for rendering within a parent container.
 */
export const RequestAccessHistoryTableApp = ({ item }) => {
    const dispatch = useDispatch();
    const [modified, setModified] = useState(false);
    const [tableId, setTableId] = useState("tbl_rah_request_access"); // Table ID for identification
    const searchId = 'txt_rah_search_request_access';
    const [results, setResults] = useState(null); // Store search results
    const option = TABLES.REQUEST_ACCESS_VISITOR;
    const [detailsModal, setDetailsModal] = useState(false);
    const [itemSelected, setItemSelected] = useState(null);
    const [bodyForExport, setBodyForExport] = useState('');
    const [searchElement, setSearchElement] = useState('');
    const [simpleSearch, setSimpleSearch] = useState(false); // Flag for simple search
    const [search, setSearch] = useState(false);

    const handleZipDownload = (ids, name) => {
        if (!ids || ids?.length === 0) {
            toast.warn(t('no_files_registered'));
            return;
        }
        const nameZip = formatName(name);
        dispatch(ZipDownload(ids)).then(({ payload: { data, code } }) => {
            if (data) {
                DownloadDataZip(data, nameZip)
            } else {
                errorMsg(code)
            }
        })
    }
    /**
     * Generates table header columns.
     * @returns {React.ReactNode[]} Array of table header cells.
     */
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} classes="first_head">{t('employee_visited')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('email')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('telephone')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('reason')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('documentation')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('request_status')}</CustomTh>
        ];
    };

    /**
     * Generates table body rows based on fetched results.
     * @returns {{ body: React.ReactNode[], data: Object[] }} Table rows and associated raw data.
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return { body: [], data: [] };

        const body = results.content.map((item, index) => {
            const visitorFullName = `${item?.requestedByName} ${item?.requestedByLastName} ${item?.requestedBySecondLastName || ""}`;
            const fullName = item?.visitToId ? `${item?.visitToName} ${item?.visitToLastName} ${item?.visitToSecondLastName || ""}` : "--";
            const statusId = item?.statusId;
            const assetsId = item?.assets?.map(asset => asset?.fileUploadDto?.id);

            return (
                <>
                    <CustomTd tableId={tableId} classes={"first-column"}>
                        <IconAvatarTable className={"mr-8px mb-1"} />
                        {fullName}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {item?.visitToEmail}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {item?.visitToPhoneNumber}
                    </CustomTd>
                    <CustomTd tableId={tableId} tooltip={item?.requestReason}>
                        <span className={"text-ellipsis"}>{item?.requestReason}</span>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <div>
                            <IconScheduleTable className={"mr-8px mb-1 icon-color-general-950"} />
                            {getDateFormat(item?.createdAt, false)}
                        </div>
                        <div>
                            <IconClockTable className={"mr-8px mb-1 icon-color-general-950"} />
                            {getTimeFormat(item?.createdAt)}
                        </div>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomImageTable
                            image={IconClip}
                            containerClass={`d-flex align-items-center ${assetsId?.length > 0 ?
                                'text-decoration-underline font-color-secondary-700 icon-color-secondary-700' :
                                'font-color-shades-500 icon-color-shades-500'}`}
                            text={`${assetsId?.length > 0 ? assetsId.length + ' ' + t('files') : '--'}`}
                            click={() => {
                                handleZipDownload(assetsId, visitorFullName + " - " + fullName);
                            }}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip
                            legend={t(status[statusId])}
                            borderColor={statusBorderColor[statusId]}
                            fontColor={statusFontColor[statusId]}
                            background={statusColor[statusId]}
                            variant={'large'}
                        />
                    </CustomTd>
                </>
            );
        });

        return {
            body,               // Generated table rows
            data: results.content // Original data
        };
    };

    /**
     * Renders dropdown action buttons for each table row.
     * @param {Object} item - Current row data.
     * @param {number} index - Row index.
     * @returns {React.ReactNode} Dropdown menu with actions for the row.
     */
    const getActionsButtons = (item, index) => (
        <DropDownMenuProfile
            openMenu={openMenu}
            handleMenuToggle={handleMenuToggle}
            menuId={`menu-${item.id}`} // Unique menu ID
        >
            <DropDownMenuItem
                id={"btn_rah_details_" + item?.id}
                index={index}
                handleClose={handleClose}
                name={t('details')}
                click={() => {
                    setItemSelected(item);
                    setDetailsModal(true);
                }}
                icon={<DetailsIconsOptions />}
            />
        </DropDownMenuProfile>
    );

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({ page, size: rowsPerPage, model: option, userId: item.id, key:searchElement });
        dispatch(SimpleSearchWithId({ body })).then(
            ({ payload: { data: { data, success } } }) => {
                if (success) {
                    setResults(data);
                }
            }
        );
    }
    // Use TableFunction to manage table logic (pagination, selection, etc.)
    const { thead, tbody, t, pagination, page, rowsPerPage,
        openMenu, handleMenuToggle, handleClose } = TableFunction({
        getTbody,
        getThead,
        data: results,
        labelRowsPerPage: 'request_per_page',
        hasContainer: true,
        fixedPagination: false,
        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
    });

    return {
        t,
        tableId,
        searchId,
        option,
        thead,
        tbody,
        detailsModal,
        setDetailsModal,
        itemSelected,
        setItemSelected,
        setBodyForExport,
        searchElement,
        setSearchElement,
        setResults,
        page,
        rowsPerPage,
        setSimpleSearch,
        search,
        setSearch,
        pagination,
        getActionsButtons,
    };
};
